import axios, {
    isAxiosError,
    AxiosHeaders,
    InternalAxiosRequestConfig,
} from "axios";
import { MISSING_TOKEN_ERROR_MSG } from "../Constants";
import { devWarn, isDev, logAxiosFailure, pathOnlyForLog } from "../utils/clientSafeLog";

// 🔐 State managed outside React
let accessToken: string | null = null;
let logUnauthorizedUrl: ((url: string) => void) | null = null;
let getApiBaseUrl: (() => string) | null = null;

export const setAccessToken = (token: string | null) => {
    accessToken = token;
};

export const getAccessToken = (): string | null => {
    return accessToken;
};

export const setUnauthorizedLogger = (fn: (url: string) => void) => {
    logUnauthorizedUrl = fn;
};

// 🆕 NEW: Allow injection of base URL getter
export const setApiBaseUrlGetter = (fn: () => string) => {
    getApiBaseUrl = fn;
};

const axiosApexInstance = axios.create({
    // For local development, ignore SSL certificate errors
    validateStatus: function (status) {
        return status >= 200 && status < 300; // Accept all 2xx status codes
    }
});

// ✅ Inject dynamic baseURL and token
axiosApexInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Check if this is a docs endpoint (open, no auth required)
        const isDocsEndpoint = config.url?.startsWith('/docs/') || config.url?.startsWith('/docs/api/');
        
        // Docs endpoints don't require auth, but still need base URL
        if (!isDocsEndpoint && !accessToken) {
            return Promise.reject(new Error(MISSING_TOKEN_ERROR_MSG));
        }

        // Inject base URL if available (uses menu setting: local vs prod)
        // Only set if caller has NOT explicitly provided a baseURL (e.g. useProdForDocs override)
        if (getApiBaseUrl && !config.baseURL) {
            config.baseURL = getApiBaseUrl();
        }

        // For local development, handle HTTPS with self-signed certificates
        // Only set default timeout if not already specified (allows long-running requests like imports)
        // PDF parsing can take longer, so we use a longer default timeout
        if (config.baseURL && config.baseURL.includes('localhost:8090') && !config.timeout) {
            // Check if this is a long-running endpoint - use 5 min timeout
            const longTimeoutPaths = ['/parse', '/statements/parse', '/task/reports/mips', '/task/reports/case-logs', '/task/mips', '/api/agi', '/api/infra'];
            const needsLongTimeout = config.url && longTimeoutPaths.some(p => config.url!.includes(p));
            if (needsLongTimeout) {
                config.timeout = 300000; // 5 minutes for parsing, reports (DB queries can be slow)
            } else {
                config.timeout = 30000; // 30 second timeout for other local dev requests
            }
        }

        if (!config.headers) {
            config.headers = new AxiosHeaders();
        }

        // Add token if available (optional for docs endpoints)
        if (accessToken) {
            if (config.headers instanceof AxiosHeaders) {
                config.headers.set("Authorization", `Bearer ${accessToken}`);
            } else {
                (config.headers as Record<string, string>)["Authorization"] = `Bearer ${accessToken}`;
            }
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// ✅ Handle 401s and suppress missing-token errors
axiosApexInstance.interceptors.response.use(
    (res) => res,
    (error) => {
        if (isAxiosError(error)) {
            if (error.message === MISSING_TOKEN_ERROR_MSG) {
                return Promise.reject(error);
            }

            // Suppress 403 errors (expected for endpoints user doesn't have access to)
            if (error.response?.status === 403) {
                return Promise.reject(error);
            }

            if (error.response?.status === 401 && logUnauthorizedUrl) {
                logUnauthorizedUrl(pathOnlyForLog(error.config?.url || "unknown"));
            }

            // Handle SSL/certificate errors for local development
            if (
                isDev &&
                error.code === 'ERR_NETWORK' &&
                error.config?.baseURL?.includes('localhost:8090')
            ) {
                devWarn(
                    'SSL/Certificate error for local development. Accept the cert or confirm the API is running.'
                );
            }

            // Never log the full axios error in production (may include Authorization / request config)
            if (error.response?.status !== 403) {
                logAxiosFailure('axios', error);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosApexInstance;
